import { AnimatePresence, motion } from 'motion/react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import {
  type UseFloatingReturn,
  FloatingFocusManager,
  offset as flOffset,
  arrow as flArrow,
  FloatingOverlay,
  useInteractions,
  type Placement,
  useMergeRefs,
  useFloating,
  autoUpdate,
  useDismiss,
  useClick,
  useRole,
  shift,
  flip,
  hide,
  size,
} from '@floating-ui/react';
import {
  type KeyboardEvent,
  type ReactElement,
  type MouseEvent,
  type ReactNode,
  type JSX,
  isValidElement,
  cloneElement,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';

import { Arrow } from '../arrow';
import { Menu, MenuData } from '../menu';

import { toRem } from '../../utils/ui';

import styles from './popover.module.scss';

export const dismissAllPopovers = () => {
  window.dispatchEvent(new CustomEvent('popover:dismissAll'));
};

export const Popover = (props: PopoverProps) => {
  const {
    menu,
    arrow,
    onOpen,
    onClose,
    width,
    content,
    maxWidth,
    children,
    arrowSize,
    placement,
    className,
    withOverlay,
    subtleShadow,
    cornerRadius,
    anchorPosition,
    offset = [0, 1],
    onContentClick,
    containerClassName,
    onAnimationComplete,
    overflowPadding = 0,
    closeOnClickOutside = true,
  } = props;

  const [open, setOpen] = useState(false);
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);

  const isControlled = props.open !== undefined;
  const isOpen = isControlled ? !!props.open : open;

  const { x, y, refs, strategy, context, middlewareData } = useFloating({
    placement,
    strategy: 'fixed',
    middleware: [
      hide({ strategy: 'referenceHidden', padding: 0 }),
      flOffset({ crossAxis: offset[0] ?? 0, mainAxis: offset[1] ?? 0, alignmentAxis: offset[2] ?? 0 }),
      flip({
        padding: overflowPadding,
        fallbackStrategy: 'bestFit',
        fallbackPlacements: [
          'top',
          'top-start',
          'top-end',
          'right',
          'right-start',
          'right-end',
          'bottom',
          'bottom-start',
          'bottom-end',
          'left',
          'left-start',
          'left-end',
        ],
      }),
      size({
        padding: overflowPadding,
        apply({ availableWidth, availableHeight, elements }) {
          const resolvedWidth = width ? Math.min(width, availableWidth) : undefined;
          const resolvedMaxWidth = maxWidth ? Math.min(maxWidth, availableWidth) : availableWidth;
          Object.assign(elements.floating.style, {
            width: resolvedWidth ? toRem(resolvedWidth) : 'max-content',
            maxWidth: toRem(resolvedMaxWidth),
            maxHeight: toRem(availableHeight),
          });
        },
      }),
      shift({ padding: overflowPadding }),
      arrow && arrowEl ? flArrow({ element: arrowEl, padding: 8 }) : undefined,
    ].filter(Boolean),
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, {
        ancestorScroll: true,
        ancestorResize: true,
        elementResize: true,
        animationFrame: true,
      }),
    open: isOpen,
    onOpenChange: (open) => {
      if (isControlled) {
        open ? onOpen?.() : onClose?.();
      } else {
        setOpen(open);
        open ? onOpen?.() : onClose?.();
      }
    },
  });

  const click = useClick(context, { keyboardHandlers: false });
  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: closeOnClickOutside,
    outsidePressEvent: 'mousedown',
  });
  const role = useRole(context, { role: menu ? 'menu' : 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const virtualRef = useMemo(() => {
    if (!props.anchorPosition) {
      return null;
    }

    return {
      getBoundingClientRect() {
        return {
          ...anchorPosition,
          width: 0,
          height: 0,
        };
      },
    };
  }, [anchorPosition]);

  useEffect(() => {
    if (virtualRef) {
      refs.setPositionReference(virtualRef as any);
    }
  }, [virtualRef, refs]);

  useEffect(() => {
    // If this popover is uncontrolled, let anchorPosition drive the open state
    if (!isControlled && 'anchorPosition' in props) {
      context.onOpenChange?.(!!anchorPosition);
    }
  }, [isControlled, isOpen, anchorPosition, context]);

  useEffect(() => {
    const handler = () => context.onOpenChange?.(false);

    window.addEventListener('popover:dismissAll', handler);

    return () => window.removeEventListener('popover:dismissAll', handler);
  }, [context]);

  const side = context.placement.split('-')[0] ?? 'bottom';

  const childRef = isValidElement(children) ? (children as any)?.ref : null;
  const mergedRef = useMergeRefs<HTMLElement>([refs.setReference, childRef]);

  const getTransformOrigin = useCallback(() => {
    const placement = context.placement.split('-')[0] as 'top' | 'right' | 'bottom' | 'left';
    const [primary] = placement.split('-');

    const originMap: Record<string, string> = {
      top: 'bottom',
      bottom: 'top',
      left: 'right',
      right: 'left',
    };

    return `${originMap[primary] || 'center'} center`;
  }, [context.placement]);

  const transform = {
    opacity: 0,
    scale: 0,
    transformOrigin: getTransformOrigin(),
  };

  const api: PopoverApi = {
    context,
    close: () => context.onOpenChange?.(false),
  };

  // If content is a function, call it with the api otherwise use it as-is
  const resolvedContent = typeof content === 'function' ? (content as (api: PopoverApi) => ReactNode)(api) : content;

  const renderContent = (
    <motion.div
      ref={refs.setFloating}
      {...getFloatingProps()}
      onClick={onContentClick}
      onAnimationComplete={onAnimationComplete}
      style={{
        position: strategy,
        top: y ?? 0,
        left: x ?? 0,
        visibility: middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
      }}
      className={clsx('popover-holder', styles.popoverHolder, containerClassName)}
      initial={transform}
      animate={{
        ...transform,
        opacity: 1,
        scale: 1,
      }}
      exit={transform}
      onContextMenu={(e: MouseEvent) => {
        if (!menu || menu.every((item) => !item.link)) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
      onDoubleClick={(e: MouseEvent) => e.stopPropagation()}
    >
      {arrow && (
        <Arrow
          data-arrow
          ref={setArrowEl}
          size={arrowSize}
          style={{
            position: 'absolute',
            top: middlewareData.arrow?.y ?? 0,
          }}
          attributes={{
            'data-popper-placement': side,
          }}
        />
      )}

      <motion.ul
        className={clsx(styles.popoverContent, styles[`cr-${cornerRadius}`], {
          [styles.subtleShadow]: subtleShadow || arrowSize === 'small',
        })}
      >
        <li className={clsx(styles.popoverInnerContent, className)}>
          {resolvedContent}
          {!!menu && <Menu data={menu} closePopover={() => context.onOpenChange(false)} />}
        </li>
      </motion.ul>
    </motion.div>
  );

  const canRenderChildren = !!children && isValidElement(children);

  return (
    <>
      {canRenderChildren &&
        cloneElement(
          children,
          getReferenceProps({
            ref: mergedRef,
            className: clsx((children.props as any).className, {
              'popover-open': isOpen,
            }),
            tabIndex: 0,
            role: 'button',
            onClick: (e) => {
              !!menu && e.stopPropagation();
              (children.props as any).onClick?.(e);
            },
            onKeyDown: (e: KeyboardEvent) => {
              (children.props as any).onKeyDown?.(e);
              if (e.key === 'Enter') {
                e.preventDefault();
                context.onOpenChange?.(!isOpen);
              }
            },
            onDoubleClick: (e: MouseEvent) => e.stopPropagation(),
          })
        )}
      {createPortal(
        <>
          <AnimatePresence>
            {isOpen && (
              <FloatingFocusManager
                context={context}
                modal // trap focus
                initialFocus={-1} // focus the floating container itself
                returnFocus
              >
                {withOverlay ? (
                  <FloatingOverlay lockScroll={false} className={styles.popoverOverlay}>
                    {renderContent}
                  </FloatingOverlay>
                ) : (
                  renderContent
                )}
              </FloatingFocusManager>
            )}
          </AnimatePresence>
        </>,
        document.body
      )}
    </>
  );
};

type PopoverApi = {
  context: UseFloatingReturn<any>['context'];
  close: () => void;
};

type PopoverContent = ReactNode | ((api: PopoverApi) => JSX.Element);

export interface PopoverProps {
  open?: boolean;
  arrow?: boolean;
  menu?: MenuData;
  offset?: number[];
  width?: number;
  maxWidth?: number;
  className?: string;
  content?: PopoverContent;
  onOpen?: () => void;
  withOverlay?: boolean;
  onClose?: () => void;
  placement?: Placement;
  children?: ReactElement;
  subtleShadow?: boolean;
  overflowPadding?: number;
  containerClassName?: string;
  closeOnClickOutside?: boolean;
  arrowSize?: 'default' | 'small';
  anchorPosition?: AnchorPosition;
  onAnimationComplete?: () => void;
  onContentClick?: (e: MouseEvent) => void;
  cornerRadius?: 'default' | 'smooth' | 'medium' | 'rounded';
}

export type AnchorPosition = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};
